export const CAMPAIGN_SCRIPT_API_PATH = "/crm/api/campaign-script/"
export const CAMPAIGN_SCRIPT_APPROVE_API_PATH =
  "/crm/api/campaign-script/approve/"

export const SCRIPT_STATUS = {
  awaitingApproval: "awaiting_approval",
  approved: "approved",
}

export const SCRIPT_STATUS_LABELS = {
  [SCRIPT_STATUS.awaitingApproval]: "Awaiting Approval",
  [SCRIPT_STATUS.approved]: "Approved",
}

export const CAMPAIGN_SCRIPT_PLACEHOLDERS = [
  { label: "First name", token: "{{First_Name}}" },
  { label: "Last name", token: "{{Last_Name}}" },
  { label: "Company", token: "{{Company}}" },
  { label: "Brand", token: "{{Brand}}" },
  { label: "Title", token: "{{Title}}" },
  { label: "City", token: "{{City}}" },
  { label: "State", token: "{{State}}" },
  { label: "Number of agents", token: "{{NoOfAgents}}" },
  { label: "Years in business", token: "{{YearsInBusiness}}" },
]

export const CAMPAIGN_SCRIPT_COMPONENTS = [
  {
    id: "heading",
    label: "Section heading",
    html: `<h2 class="campaign-script-heading" data-campaign-component="heading" style="color: #892fce;">Section title</h2>`,
  },
  {
    id: "callout",
    label: "Callout box",
    html: `<div class="campaign-script-callout" data-campaign-component="callout"><p><strong>Note:</strong> Add a highlighted reminder for the caller.</p></div>`,
  },
  {
    id: "script-block",
    label: "Script block",
    html: `<div class="campaign-script-block" data-campaign-component="script-block"><p>What the caller should say goes here.</p></div>`,
  },
  {
    id: "steps",
    label: "Numbered call steps",
    html: `<ol class="campaign-script-steps" data-campaign-component="steps"><li>Introduce yourself and the brokerage.</li><li>Confirm they have a moment to talk.</li><li>Share the opportunity and next step.</li></ol>`,
  },
  {
    id: "divider",
    label: "Divider",
    html: `<hr class="campaign-script-divider" data-campaign-component="divider" />`,
  },
]

export const CAMPAIGN_SCRIPT_EDITOR_CONTENT_STYLE = `
  body { font-family: Inter, Arial, sans-serif; font-size: 14px; line-height: 1.55; color: #344054; margin: 12px 16px; }
  h1, h2, h3 { color: #892fce; margin: 0.6em 0 0.4em; }
  .campaign-script-heading { color: #892fce; }
  .campaign-script-callout {
    background: #f8f1ff;
    border-left: 4px solid #892fce;
    padding: 10px 14px;
    border-radius: 8px;
    margin: 12px 0;
  }
  .campaign-script-block {
    background: #f9fafb;
    border: 1px dashed #d0d5dd;
    padding: 12px 14px;
    border-radius: 8px;
    margin: 12px 0;
  }
  .campaign-script-steps { padding-left: 1.4em; }
  .campaign-script-divider { border: none; border-top: 1px solid #d0d5dd; margin: 16px 0; }
  .campaign-script-placeholder { color: #892fce; }
`

export const CAMPAIGN_SCRIPT_PREVIEW_SX = {
  fontFamily: "Inter, Arial, sans-serif",
  fontSize: 14,
  lineHeight: 1.55,
  color: "#344054",
  "& h1, & h2, & h3": { color: "#892fce", margin: "0.6em 0 0.4em" },
  "& .campaign-script-heading": { color: "#892fce" },
  "& .campaign-script-callout": {
    background: "#f8f1ff",
    borderLeft: "4px solid #892fce",
    padding: "10px 14px",
    borderRadius: "8px",
    margin: "12px 0",
  },
  "& .campaign-script-block": {
    background: "#f9fafb",
    border: "1px dashed #d0d5dd",
    padding: "12px 14px",
    borderRadius: "8px",
    margin: "12px 0",
  },
  "& .campaign-script-steps": { paddingLeft: "1.4em" },
  "& .campaign-script-divider": {
    border: "none",
    borderTop: "1px solid #d0d5dd",
    margin: "16px 0",
  },
  "& .campaign-script-placeholder": { color: "#892fce" },
}

export function wrapPlaceholder(token) {
  return `<strong class="campaign-script-placeholder">${token}</strong>`
}

const STYLE_BLOCK_RE = /<style\b[^>]*>[\s\S]*?<\/style>/gi

export function createStyleProtectRe() {
  return new RegExp("<style[\\s\\S]*?<\\/style>", "gi")
}

export function extractStyleBlocks(html) {
  return [...(html || "").matchAll(STYLE_BLOCK_RE)].map((match) => match[0])
}

export function extractEmbeddedCss(html) {
  return [...(html || "").matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi)]
    .map((match) => match[1])
    .join("\n")
}

const TINYMCE_ESCAPED_LINE_WRAP_RE =
  /<div><span>\s*([\s\S]*?)\s*<\/span><\/div>/gi
const FULL_HTML_DOCUMENT_RE = /<!DOCTYPE\s+html|<html[\s>]/i

export function isEscapedHtmlContent(html) {
  if (!html || typeof html !== "string") {
    return false
  }
  const escapedTags = (html.match(/&lt;\/?[a-z][^&]*?&gt;/gi) || []).length
  const realTags = (html.match(/<\/?[a-z][\s>/]/gi) || []).length
  return escapedTags >= 3 && escapedTags > realTags
}

export function decodeHtmlEntities(text) {
  if (!text) {
    return ""
  }
  if (typeof document !== "undefined") {
    const textarea = document.createElement("textarea")
    textarea.innerHTML = text
    return textarea.value
  }
  return text
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, "&")
}

export function repairEscapedHtmlContent(html) {
  if (!isEscapedHtmlContent(html)) {
    return html || ""
  }
  const unwrapped = html.replace(TINYMCE_ESCAPED_LINE_WRAP_RE, "$1\n").trim()
  return decodeHtmlEntities(unwrapped)
}

export function isLikelyRawHtmlPaste(text) {
  const trimmed = (text || "").trim()
  if (!trimmed) {
    return false
  }
  if (isEscapedHtmlContent(trimmed)) {
    return true
  }
  if (/<!DOCTYPE\s+html/i.test(trimmed)) {
    return true
  }
  if (/<html[\s>]/i.test(trimmed)) {
    return true
  }
  if (/<head[\s>]/i.test(trimmed)) {
    return true
  }
  if (/<body[\s>]/i.test(trimmed)) {
    return true
  }
  return false
}

export function isLikelyHtmlFragmentPaste(text) {
  const trimmed = (text || "").trim()
  if (!trimmed || trimmed.length < 20) {
    return false
  }
  return (
    /^<(div|table|h[1-6]|style|section|article|main|header|footer|ul|ol)\b/i.test(
      trimmed
    ) &&
    /<\/(div|table|h[1-6]|style|section|article|main|header|footer|ul|ol)>/i.test(
      trimmed
    )
  )
}

export function shouldTreatPasteAsHtml(text) {
  return isLikelyRawHtmlPaste(text) || isLikelyHtmlFragmentPaste(text)
}

export function extractBodyHtml(html) {
  const source = html || ""
  const withoutStyles = source.replace(STYLE_BLOCK_RE, "")
  const bodyMatch = withoutStyles.match(/<body\b[^>]*>([\s\S]*?)<\/body>/i)
  return bodyMatch ? bodyMatch[1] : withoutStyles
}

export function normalizePastedCampaignScriptHtml(html) {
  const repaired = repairEscapedHtmlContent((html || "").trim())
  if (!repaired) {
    return ""
  }
  if (!isLikelyRawHtmlPaste(repaired) && !isLikelyHtmlFragmentPaste(repaired)) {
    return repaired
  }

  if (typeof DOMParser !== "undefined") {
    const doc = new DOMParser().parseFromString(repaired, "text/html")
    const hasDocumentStructure =
      doc.querySelector("html") ||
      doc.querySelector("body") ||
      FULL_HTML_DOCUMENT_RE.test(repaired)
    if (hasDocumentStructure) {
      const styles = Array.from(doc.querySelectorAll("style"))
        .map((node) => node.outerHTML)
        .join("\n")
      const bodyHtml = doc.body?.innerHTML?.trim() || repaired
      return styles ? `${styles}\n${bodyHtml}` : bodyHtml
    }
  }

  const styles = extractStyleBlocks(repaired).join("\n")
  const body = extractBodyHtml(repaired)
  if (FULL_HTML_DOCUMENT_RE.test(repaired) || /<body[\s>]/i.test(repaired)) {
    return styles ? `${styles}\n${body}` : body
  }
  return repaired
}

export function prepareCampaignScriptContent(html) {
  const source = html || ""
  if (isEscapedHtmlContent(source) || FULL_HTML_DOCUMENT_RE.test(source)) {
    return normalizePastedCampaignScriptHtml(source)
  }
  return source
}

export function applyVisualBodyToSource(originalHtml, visualBody) {
  const source = originalHtml || ""
  if (/<body\b/i.test(source)) {
    return source.replace(
      /(<body\b[^>]*>)([\s\S]*?)(<\/body>)/i,
      `$1${visualBody}$3`
    )
  }
  const styles = extractStyleBlocks(source).join("\n")
  return styles ? `${styles}\n${visualBody}` : visualBody
}

export function sanitizePreviewHtml(html) {
  if (typeof window === "undefined" || typeof DOMParser === "undefined") {
    return html || ""
  }
  const doc = new DOMParser().parseFromString(html || "", "text/html")
  doc
    .querySelectorAll("script, iframe, object, embed, link, meta")
    .forEach((el) => el.remove())
  doc.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      const name = attr.name.toLowerCase()
      const value = (attr.value || "").trim().toLowerCase()
      // Strip inline handlers and javascript: URLs from unsaved preview HTML.
      if (
        name.startsWith("on") ||
        // eslint-disable-next-line no-script-url
        value.startsWith("javascript:")
      ) {
        el.removeAttribute(attr.name)
      }
    })
  })
  const styles = Array.from(doc.querySelectorAll("style"))
    .map((node) => node.outerHTML)
    .join("")
  return `${styles}${doc.body.innerHTML}`
}

export function formatScriptTimestamp(iso) {
  if (!iso) {
    return null
  }
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    })
  } catch {
    return null
  }
}
